import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../lib/api';
import { DollarSign, TrendingUp, Users, CheckCircle2, Clock, Loader2, RefreshCw, Wallet, BarChart3 } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';

const fmtMoney = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const fmtMonth = (key) => {
  // key: YYYY-MM
  try {
    const [y, m] = key.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  } catch { return key; }
};

const KpiCard = ({ icon: Icon, label, value, accent = 'green', hint }) => {
  const map = {
    green: 'bg-[#E1F3E4] text-[#0B5E2A]',
    gold: 'bg-[#FEF4DB] text-[#8a5e00]',
    blue: 'bg-[#DEE9FA] text-[#1e40af]',
    rose: 'bg-[#FDE2E2] text-[#9b1c1c]',
  };
  return (
    <div className="bg-white border border-[#0B5E2A]/10 rounded-xl p-5">
      <div className="flex items-center gap-2.5">
        <div className={`h-8 w-8 rounded-md flex items-center justify-center ${map[accent]}`}><Icon className="h-4 w-4" /></div>
        <div className="text-sm font-semibold text-[#4b5e4d]">{label}</div>
      </div>
      <div className="mt-3 font-display text-3xl font-bold text-[#1b2a1f]">{value}</div>
      {hint && <div className="mt-1 text-xs text-[#6b7e6d]">{hint}</div>}
    </div>
  );
};

const CommissionsTab = ({ filter = '' }) => {
  const [loading, setLoading] = useState(true);
  const [commissions, setCommissions] = useState(null);
  const [forecast, setForecast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, f] = await Promise.all([
        api.get('/admin/commissions'),
        api.get('/admin/revenue-forecast'),
      ]);
      setCommissions(c.data);
      setForecast(f.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="py-16 text-center text-[#6b7e6d]" data-testid="commissions-loading">
        <Loader2 className="h-6 w-6 mx-auto animate-spin" />
      </div>
    );
  }

  const rows = (commissions?.rows || []).filter((r) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (r.name || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.company || '').toLowerCase().includes(q);
  });
  const totals = commissions?.totals || { earned: 0, paid: 0, pending: 0, net_margin: 0 };
  const series = (forecast?.series || []).map((s) => ({ ...s, monthLabel: fmtMonth(s.month) }));

  return (
    <div className="space-y-6" data-testid="commissions-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={DollarSign} label="Commissions Earned" value={fmtMoney(totals.earned)} accent="green" hint="All assigned students (non-rejected)" />
        <KpiCard icon={CheckCircle2} label="Paid Out" value={fmtMoney(totals.paid)} accent="blue" hint="Enrolled students only" />
        <KpiCard icon={Clock} label="Pending" value={fmtMoney(totals.pending)} accent="gold" hint="Not yet enrolled" />
        <KpiCard icon={TrendingUp} label="Net Margin (Internal)" value={fmtMoney(totals.net_margin)} accent="rose" hint="Sale − college fee − commission" />
      </div>

      {/* Revenue Forecast Chart */}
      <div className="bg-white border border-[#0B5E2A]/10 rounded-xl p-5 md:p-6" data-testid="revenue-forecast-chart">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 brand-green" />
              <h3 className="font-display text-xl text-[#0B5E2A]">Revenue Forecast · Last 12 Months</h3>
            </div>
            <p className="text-xs text-[#6b7e6d] mt-1">Gross sale price vs. college fees and recruiter commissions, plus net margin trend.</p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-1.5 text-xs text-[#0B5E2A] hover:bg-[#f0f6ee] px-3 py-1.5 rounded-md border border-[#0B5E2A]/20">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
        {series.every((s) => s.gross === 0) ? (
          <div className="py-14 text-center text-sm text-[#6b7e6d]">
            No revenue data yet. Assign students to colleges with a sale price to see the trend here.
          </div>
        ) : (
          <div className="h-[320px] w-full">
            <ResponsiveContainer>
              <ComposedChart data={series} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d9e6d9" />
                <XAxis dataKey="monthLabel" stroke="#4b5e4d" fontSize={12} />
                <YAxis stroke="#4b5e4d" fontSize={12} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
                <Tooltip
                  contentStyle={{ background: 'white', border: '1px solid #0B5E2A20', borderRadius: 8, fontSize: 12 }}
                  formatter={(v, n) => [fmtMoney(v), n]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="gross" name="Gross" fill="#0B5E2A" radius={[4, 4, 0, 0]} />
                <Bar dataKey="college_fees" name="College Fees" fill="#6b9a7a" radius={[4, 4, 0, 0]} />
                <Bar dataKey="recruiter_commissions" name="Commissions" fill="#F0B93C" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="net_margin" name="Net Margin" stroke="#9b1c1c" strokeWidth={2.5} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Per-recruiter breakdown */}
      <div className="bg-white border border-[#0B5E2A]/10 rounded-xl overflow-hidden" data-testid="commissions-breakdown-table">
        <div className="px-5 py-4 border-b border-[#0B5E2A]/10 flex items-center gap-2">
          <Wallet className="h-5 w-5 brand-green" />
          <h3 className="font-display text-lg text-[#0B5E2A]">Per-Recruiter Commissions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f0f6ee] text-[#0B5E2A]">
              <tr>
                <Th>Recruiter</Th>
                <Th>Company</Th>
                <Th className="text-center">Rate / Student</Th>
                <Th className="text-center">Assigned</Th>
                <Th className="text-center">Admitted</Th>
                <Th className="text-center">Enrolled</Th>
                <Th className="text-right">Earned</Th>
                <Th className="text-right">Paid</Th>
                <Th className="text-right pr-6">Pending</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={9} className="text-center py-10 text-[#6b7e6d]">No recruiters match.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.recruiter_id} className="border-t border-[#0B5E2A]/10 hover:bg-[#faf8ee]">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0B5E2A]">{r.name}</div>
                    <div className="text-xs text-[#6b7e6d]">{r.email}</div>
                  </td>
                  <td className="px-4 py-3">{r.company || <span className="text-[#6b7e6d] italic">—</span>}</td>
                  <td className="px-4 py-3 text-center">{r.commission_per_student ? fmtMoney(r.commission_per_student) : <span className="text-[#6b7e6d] italic">—</span>}</td>
                  <td className="px-4 py-3 text-center"><Badge tone="blue">{r.students_assigned}</Badge></td>
                  <td className="px-4 py-3 text-center"><Badge tone="gold">{r.students_admitted}</Badge></td>
                  <td className="px-4 py-3 text-center"><Badge tone="green">{r.students_enrolled}</Badge></td>
                  <td className="px-4 py-3 text-right font-semibold text-[#1b2a1f]">{fmtMoney(r.earned)}</td>
                  <td className="px-4 py-3 text-right text-[#0B5E2A] font-semibold">{fmtMoney(r.paid)}</td>
                  <td className="px-4 py-3 text-right pr-6 text-[#8a5e00] font-semibold">{fmtMoney(r.pending)}</td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-[#f0f6ee] text-[#0B5E2A] font-bold">
                <tr>
                  <td className="px-4 py-3" colSpan={6}>Totals</td>
                  <td className="px-4 py-3 text-right">{fmtMoney(totals.earned)}</td>
                  <td className="px-4 py-3 text-right">{fmtMoney(totals.paid)}</td>
                  <td className="px-4 py-3 text-right pr-6">{fmtMoney(totals.pending)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

const Th = ({ children, className = '' }) => <th className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider ${className}`}>{children}</th>;
const Badge = ({ children, tone = 'blue' }) => {
  const tones = {
    blue: 'bg-[#DEE9FA] text-[#1e40af]',
    gold: 'bg-[#FEF4DB] text-[#8a5e00]',
    green: 'bg-[#E1F3E4] text-[#0B5E2A]',
  };
  return <span className={`inline-flex h-7 min-w-[28px] px-2 items-center justify-center rounded-full text-xs font-bold ${tones[tone]}`}>{children}</span>;
};

export default CommissionsTab;
